/**
 * Users Management Page
 *
 * Enterprise features for billion-scale:
 * - Cursor-based pagination (no offset performance issues)
 * - Debounced search with request cancellation
 * - Virtual scrolling ready
 * - Optimistic updates
 * - Request deduplication
 * - Proper error boundaries
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Button from '../../components/common/Button';
import { UserDrawer } from '../../components/modals';
import { usersApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation, useDebouncedValue, useIntersectionObserver } from '../../hooks/useDataFetch';
import { requestManager } from '../../lib/RequestManager';
import './Users.css';

// Constants
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const Users = () => {
  const { showToast } = useToast();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);

  // Pagination state for cursor-based pagination
  const [cursor, setCursor] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  // Debounced search
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  // Build query params
  const queryParams = useMemo(() => ({
    limit: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(statusFilter && { status: statusFilter }),
    ...(roleFilter && { role: roleFilter }),
    ...(cursor && { cursor }),
  }), [debouncedSearch, statusFilter, roleFilter, cursor]);

  // Cache key for request deduplication
  const cacheKey = useMemo(() =>
    `users:${debouncedSearch}:${statusFilter}:${roleFilter}:${cursor}`,
    [debouncedSearch, statusFilter, roleFilter, cursor]
  );

  // Fetch users
  const {
    data: usersResponse,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    () => usersApi.list(queryParams),
    {
      key: cacheKey,
      cacheTTL: 30000,
      enabled: true,
      onSuccess: (response) => {
        const newUsers = response.users || response.data || response.items || [];
        const nextCursor = response.nextCursor || response.pagination?.nextCursor;

        if (cursor) {
          // Append for pagination
          setAllUsers(prev => [...prev, ...newUsers]);
        } else {
          // Replace for new search/filter
          setAllUsers(newUsers);
        }

        setHasMore(!!nextCursor && newUsers.length === PAGE_SIZE);
      },
      onError: (err) => {
        showToast(err.message || 'Failed to load users', 'error');
      },
    }
  );

  // Reset when filters change
  useEffect(() => {
    setCursor(null);
    setAllUsers([]);
    setHasMore(true);
    // Invalidate cache for this query pattern
    requestManager.invalidateCache(`users:${debouncedSearch}:${statusFilter}:${roleFilter}`);
  }, [debouncedSearch, statusFilter, roleFilter]);

  // Infinite scroll observer
  const [loadMoreRef, isNearBottom] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '200px',
  });

  // Load more when scrolling
  useEffect(() => {
    if (isNearBottom && hasMore && !isLoading && !isFetching && allUsers.length > 0) {
      const lastUser = allUsers[allUsers.length - 1];
      if (lastUser?.id) {
        setCursor(lastUser.id);
      }
    }
  }, [isNearBottom, hasMore, isLoading, isFetching, allUsers]);

  // Mutations
  const suspendMutation = useMutation(
    (userId) => usersApi.update(userId, { status: 'suspended' }),
    {
      onSuccess: (_, userId) => {
        setAllUsers(prev =>
          prev.map(u => u.id === userId ? { ...u, status: 'suspended' } : u)
        );
        showToast('User suspended', 'success');
      },
      onError: (err) => {
        showToast(err.message || 'Failed to suspend user', 'error');
      },
      invalidateKeys: ['users'],
    }
  );

  const activateMutation = useMutation(
    (userId) => usersApi.update(userId, { status: 'active' }),
    {
      onSuccess: (_, userId) => {
        setAllUsers(prev =>
          prev.map(u => u.id === userId ? { ...u, status: 'active' } : u)
        );
        showToast('User activated', 'success');
      },
      onError: (err) => {
        showToast(err.message || 'Failed to activate user', 'error');
      },
      invalidateKeys: ['users'],
    }
  );

  const deleteMutation = useMutation(
    (userId) => usersApi.delete(userId),
    {
      onMutate: (userId) => {
        // Optimistic update - remove from UI immediately
        const previousUsers = [...allUsers];
        setAllUsers(prev => prev.filter(u => u.id !== userId));

        // Return rollback function
        return () => setAllUsers(previousUsers);
      },
      onSuccess: () => {
        showToast('User deleted', 'success');
      },
      onError: (err) => {
        showToast(err.message || 'Failed to delete user', 'error');
      },
      invalidateKeys: ['users'],
    }
  );

  // Handlers
  const handleViewUser = useCallback((user) => {
    setSelectedUser(user);
    setIsUserDrawerOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    setCursor(null);
    setAllUsers([]);
    requestManager.invalidateCache('users');
    refetch();
  }, [refetch]);

  const handleStatusChange = useCallback((userId, currentStatus) => {
    if (currentStatus === 'active') {
      suspendMutation.mutate(userId);
    } else {
      activateMutation.mutate(userId);
    }
  }, [suspendMutation, activateMutation]);

  const handleDelete = useCallback((userId, userName) => {
    if (window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      deleteMutation.mutate(userId);
    }
  }, [deleteMutation]);

  // Format helpers
  const formatLastLogin = (timestamp) => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Memoized stats
  const stats = useMemo(() => ({
    total: allUsers.length + (hasMore ? '+' : ''),
    active: allUsers.filter(u => u.status === 'active').length,
    suspended: allUsers.filter(u => u.status === 'suspended').length,
  }), [allUsers, hasMore]);

  return (
    <div className="users-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">
            Manage users across all tenants
            {allUsers.length > 0 && (
              <span className="users-count"> ({stats.total} loaded)</span>
            )}
          </p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={handleRefresh}
            disabled={isLoading || isFetching}
          >
            {isFetching ? 'Syncing...' : 'Sync'}
          </Button>
          <Button variant="primary" icon="plus">Add User</Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="users-stats">
        <div className="stat-pill">
          <span className="stat-dot active"></span>
          <span>{stats.active} Active</span>
        </div>
        <div className="stat-pill">
          <span className="stat-dot suspended"></span>
          <span>{stats.suspended} Suspended</span>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="users-error">
          <span className="icon"><svg><use href="#icon-alert-triangle"></use></svg></span>
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>Retry</Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="users-toolbar">
        <div className="search-box">
          <span className="icon"><svg><use href="#icon-search"></use></svg></span>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search users"
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <svg><use href="#icon-x"></use></svg>
            </button>
          )}
        </div>
        <div className="filter-group">
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
          <select
            className="filter-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label="Filter by role"
          >
            <option value="">All Roles</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="users-table-container">
        <table className="users-table" role="grid">
          <thead>
            <tr>
              <th scope="col">User</th>
              <th scope="col">Tenant</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Last Login</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Loading skeleton */}
            {isLoading && allUsers.length === 0 && (
              [...Array(10)].map((_, i) => (
                <tr key={`skeleton-${i}`} className="skeleton-row">
                  <td>
                    <div className="user-cell">
                      <div className="skeleton-avatar"></div>
                      <div className="skeleton-info">
                        <div className="skeleton-line" style={{ width: '120px' }}></div>
                        <div className="skeleton-line short" style={{ width: '180px' }}></div>
                      </div>
                    </div>
                  </td>
                  <td><div className="skeleton-line" style={{ width: '100px' }}></div></td>
                  <td><div className="skeleton-badge"></div></td>
                  <td><div className="skeleton-badge"></div></td>
                  <td><div className="skeleton-line" style={{ width: '80px' }}></div></td>
                  <td><div className="skeleton-actions"></div></td>
                </tr>
              ))
            )}

            {/* User rows */}
            {allUsers.map((user) => (
              <tr key={user.id} data-user-id={user.id}>
                <td>
                  <div className="user-cell">
                    <div className="user-avatar-sm" data-status={user.status}>
                      {getUserInitials(user.name)}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{user.name || 'Unknown'}</div>
                      <div className="user-email">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className="tenant-name">
                    {user.tenant?.name || user.tenantName || user.company || '-'}
                  </span>
                </td>
                <td>
                  <span className={`role-badge role-${(user.role || 'user').toLowerCase()}`}>
                    {user.role || 'User'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${user.status || 'active'}`}>
                    {user.status || 'Active'}
                  </span>
                </td>
                <td className="text-tertiary">
                  {formatLastLogin(user.lastLoginAt || user.lastLogin)}
                </td>
                <td>
                  <div className="action-buttons">
                    <Button
                      variant="ghost"
                      size="xs"
                      icon="eye"
                      iconOnly
                      aria-label={`View ${user.name}`}
                      onClick={() => handleViewUser(user)}
                    />
                    <Button
                      variant="ghost"
                      size="xs"
                      icon={user.status === 'suspended' ? 'play' : 'pause'}
                      iconOnly
                      aria-label={user.status === 'suspended' ? 'Activate' : 'Suspend'}
                      onClick={() => handleStatusChange(user.id, user.status)}
                      disabled={suspendMutation.isLoading || activateMutation.isLoading}
                    />
                    <Button
                      variant="ghost"
                      size="xs"
                      icon="trash"
                      iconOnly
                      aria-label={`Delete ${user.name}`}
                      onClick={() => handleDelete(user.id, user.name)}
                      disabled={deleteMutation.isLoading}
                    />
                  </div>
                </td>
              </tr>
            ))}

            {/* Empty state */}
            {!isLoading && allUsers.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <div className="users-empty">
                    <span className="icon"><svg><use href="#icon-users"></use></svg></span>
                    <h3>No users found</h3>
                    <p>
                      {searchQuery || statusFilter || roleFilter
                        ? 'Try adjusting your search or filters'
                        : 'Get started by adding your first user'}
                    </p>
                    {!searchQuery && !statusFilter && !roleFilter && (
                      <Button variant="primary" icon="plus">Add User</Button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Load more trigger for infinite scroll */}
        {hasMore && allUsers.length > 0 && (
          <div ref={loadMoreRef} className="load-more-trigger">
            {isFetching && (
              <div className="loading-more">
                <div className="spinner"></div>
                <span>Loading more users...</span>
              </div>
            )}
          </div>
        )}

        {/* End of list */}
        {!hasMore && allUsers.length > 0 && (
          <div className="end-of-list">
            All {allUsers.length} users loaded
          </div>
        )}
      </div>

      {/* User Drawer */}
      <UserDrawer
        isOpen={isUserDrawerOpen}
        onClose={() => setIsUserDrawerOpen(false)}
        user={selectedUser}
        onUserUpdate={(updatedUser) => {
          setAllUsers(prev =>
            prev.map(u => u.id === updatedUser.id ? updatedUser : u)
          );
        }}
      />
    </div>
  );
};

export default Users;
